import React, { useState, useEffect } from "react";
import { AuthorProps } from "../../definitions/interfaces/GlobalInterfaces";
import { colors } from "../../definitions/constants/GlobalStyles";
import { useMediaQuery, Flex, Box, Text } from "@chakra-ui/react";
import i18n from "../../i18n/i18n";
import {
  CommentsIcon,
  ShareIcon,
} from "../../definitions/constants/IconsComponent";
import IconSoccerBall from "../../assets/icons/IconSoccerBall";
import { postStore, appStore } from "../../zustand/globalStore";
import { useNavigate } from "react-router-dom";
import { consumeApi } from "../../utils/tools";

export function ShareCount({ post }: AuthorProps) {
  const [isMobile] = useMediaQuery("(max-width: 992px)");

  const navigate = useNavigate();
  const { userData } = appStore((state) => state);
  const { setSelectedPost, setIsShareModalOpen, setShowCommentsModal } = postStore((state) => state);

  const initialLikeCount = post.countLikes !== undefined ? post.countLikes : (post.num_like || 0);
  const [likeCount, setLikeCount] = useState<number>(initialLikeCount);
  const [isLiked, setIsLiked] = useState<boolean>(post.like_user || false);

  useEffect(() => {
    setLikeCount(post.countLikes !== undefined ? post.countLikes : (post.num_like || 0));
    setIsLiked(post.like_user || false);
  }, [post]);

  const handleShareClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!userData) {
      navigate("/login");
      return;
    }
    setSelectedPost(post);
    setIsShareModalOpen(true);
  };

  const handleLikeClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!userData) {
      navigate("/login");
      return;
    }
    const newIsLiked = !isLiked;
    setIsLiked(newIsLiked);
    setLikeCount((prev) => (newIsLiked ? prev + 1 : Math.max(0, prev - 1)));

    try {
      const endpoint = newIsLiked ? "posts/like" : "posts/unlike";
      await consumeApi(endpoint, "POST", {
        user_id: userData.id,
        post_id: post.id,
      });
    } catch (error) {
      // Revert on error
      setIsLiked(!newIsLiked);
      setLikeCount((prev) => (!newIsLiked ? prev + 1 : Math.max(0, prev - 1)));
      console.error("Error toggling like", error);
    }
  };

  const handleCommentClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!userData) {
      navigate("/login");
      return;
    }
    setSelectedPost(post);
    setShowCommentsModal(true);
  };

  return (
    <Flex
      gap={2}
      align="start"
      justify="start"
      marginTop={5}
      p={isMobile ? "2" : "0"}
    >
      <Box>
        <Flex gap={1} align="center" justify="center" cursor="pointer" onClick={handleLikeClick}>
          <Box>
            <IconSoccerBall stroke={isLiked ? colors.figmaColors.secColor : colors.backgrounds.black} />
          </Box>
          <Text
            fontSize="13px"
            fontWeight="600"
            lineHeight="18.51px"
            color={isLiked ? colors.figmaColors.secColor : colors.figmaColors.black}
          >
            {likeCount === 0
              ? `0 ${i18n.t("likes")}`
              : likeCount === 1
                ? `${likeCount} ${i18n.t("like")}`
                : `${likeCount} ${i18n.t("likes")}`}
          </Text>
        </Flex>
      </Box>
      <Box ml={4}>
        <Flex gap={1} align="center" justify="center" cursor="pointer" onClick={handleCommentClick}>
          <Box>
            <CommentsIcon />
          </Box>
          <Text
            fontSize="13px"
            fontWeight="600"
            lineHeight="18.51px"
            color={colors.figmaColors.black}
          >
            {post.num_comments === undefined || post.num_comments === 0
              ? `0 ${i18n.t("comments")}`
              : post.num_comments === 1
                ? `${post.num_comments} ${i18n.t("comment")}`
                : `${post.num_comments} ${i18n.t("comments")}`}
          </Text>
        </Flex>
      </Box>

      <Box marginLeft="auto">
        <Flex gap={1} align="center" justify="center">
          <Box>
            <ShareIcon />
          </Box>
          <Text
            fontSize="13px"
            fontWeight="600"
            lineHeight="18.51px"
            color={colors.figmaColors.secColor}
            cursor="pointer"
            onClick={handleShareClick}
          >
            {i18n.t("share")}
          </Text>
        </Flex>
      </Box>
    </Flex>
  );
}

export default ShareCount;
